import { z } from 'zod';
import { INTELLIGENCE_LEVELS, intelligenceLevelSchema } from './admin.js';
import { productKeySchema } from './products.js';
/**
 * Milestone 3 contracts (0.9.0): plans and plan versions with typed price components, token estimates, the
 * billable basis, contracts with their trial and spend cap, usage events, reservations, adjustments, rated
 * periods with their line items, billing documents and the events of the billing provider.
 * Design: docs/design/entitlements-usage-billing.md sections 2 to 6.
 *
 * Units, everywhere: money is an integer number of micros of the currency (1 USD = 1,000,000 micros), never a
 * float; tokens are integer millitokens (1 Trace token = 1,000 millitokens), so the level multipliers (5.1 tokens
 * for a booking at Steady) stay exact. Every amount and count below is a safe integer.
 */
export const MICROS_PER_UNIT = 1_000_000;
export const MILLITOKENS_PER_TOKEN = 1_000;
/** Currencies a plan version may bill in. Both have two decimals at the provider (cents). */
export const BILLING_CURRENCIES = ['USD', 'CAD'];
export const billingCurrencySchema = z.enum(BILLING_CURRENCIES);
/** A signed amount in micros (credits and discounts are negative line items). */
export const microsSchema = z.number().int().safe();
const nonNegativeMicros = microsSchema.nonnegative();
const positiveMicros = microsSchema.positive();
/** A token count in millitokens. */
export const millitokensSchema = z.number().int().safe().nonnegative();
const sha256Hex = z.string().regex(/^[0-9a-f]{64}$/, 'a sha256 digest in lowercase hex');
const reason = z.string().min(3).max(500);
/** A component key, unique within a plan version (for example base, tokens, carrier_connections). */
const componentKey = z.string().regex(/^[a-z][a-z0-9_]{0,62}$/);
/** A module of the catalog, product first (enterprise.quoting). */
const moduleKey = z.string().regex(/^[a-z]+\.[a-z_]+$/, 'product.module, for example enterprise.quoting');
/** Producer-supplied identifiers: 8 to 200 characters, letters, digits and . _ : - only (no spaces, no controls). */
const producerKey = z
    .string()
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{7,199}$/, '8 to 200 of A-Z a-z 0-9 . _ : -');
/* ------------------------------------------------------------------------------------------------ */
/* Billable actions and units                                                                        */
/* ------------------------------------------------------------------------------------------------ */
/**
 * The billable actions: units of work that use Trace tokens, priced by the plan version's token estimates. They
 * are not the side effects of the module catalog (send_quote_request and so on): one quoting of a shipment may
 * send many requests and is billed once.
 */
export const BILLABLE_ACTIONS = [
    'quoting',
    'negotiation',
    'booking',
    'tracking',
    'email_intake',
    'lane_check',
    'text_message',
    'call_minute',
    /** Unreleased: one answer of a product's assistant, however many steps it took (design section 6 item 17). */
    'assistant_reply',
];
export const billableActionSchema = z.enum(BILLABLE_ACTIONS);
/**
 * Actions that count once per shipment: their uniqueness key is (organization, shipment reference, action), and the
 * event belongs to the period of its first occurrence. Every other action counts per occurrence (tracking, intake,
 * lane checks, texts, call minutes).
 */
export const ONCE_PER_SHIPMENT_ACTIONS = [
    'quoting',
    'negotiation',
    'booking',
];
export function isOncePerShipment(action) {
    return ONCE_PER_SHIPMENT_ACTIONS.includes(action);
}
/** What a usage event refers to. A shipment is also a broker's load. */
export const USAGE_REFERENCE_TYPES = ['shipment', 'task', 'message', 'call', 'lane'];
export const usageReferenceSchema = z.strictObject({
    type: z.enum(USAGE_REFERENCE_TYPES),
    id: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,199}$/, '1 to 200 of A-Z a-z 0-9 . _ : -'),
});
/** The one metered unit: Trace tokens. */
export const USAGE_UNITS = ['trace_token'];
/** What a licensed quantity counts. */
export const LICENSED_UNITS = ['carrier_connection'];
/* ------------------------------------------------------------------------------------------------ */
/* Price components                                                                                  */
/* ------------------------------------------------------------------------------------------------ */
export const PRICE_COMPONENT_KINDS = [
    'flat_recurring',
    'licensed_quantity',
    'metered_curve',
    'metered_graduated',
    'conditional_discount',
];
/**
 * The version of the pure function that prices each component kind. A rated line item records it
 * (detail.priceFunction, for example metered_curve@1), so a period rated before a formula changes stays
 * explainable. Changing a formula is a new version here, never an edit.
 */
export const PRICE_FUNCTION_VERSIONS = {
    flat_recurring: 1,
    licensed_quantity: 1,
    metered_curve: 1,
    metered_graduated: 1,
    conditional_discount: 1,
};
const label = z.string().min(2).max(120);
/** A fixed amount per period (the base: everyone in the organization, the platform). */
export const flatRecurringSchema = z.strictObject({
    kind: z.literal('flat_recurring'),
    key: componentKey,
    label,
    amountMicros: nonNegativeMicros,
});
/** Unit price times the purchased quantity (the contract's) per period; the active count may not exceed it. */
export const licensedQuantitySchema = z.strictObject({
    kind: z.literal('licensed_quantity'),
    key: componentKey,
    label,
    unit: z.enum(LICENSED_UNITS),
    unitPriceMicros: nonNegativeMicros,
    /**
     * Unreleased: the most a contract may purchase (the pricing page sells up to 30 carrier connections). Signing or
     * changing a contract past it is refused; absent means no limit.
     */
    maxQuantity: z.number().int().positive().max(100_000).optional(),
});
/**
 * Metered tokens on a falling curve. The next token costs floor + (start - floor) * e^(-T / scale), so the period's
 * cost of T tokens is cost(T) = floor * T + (start - floor) * scale * (1 - e^(-T / scale)). Prices are micros per
 * token; scale is in tokens. Evaluated in double precision once per line item and rounded half-even to micros.
 */
export const meteredCurveSchema = z
    .strictObject({
    kind: z.literal('metered_curve'),
    key: componentKey,
    label,
    unit: z.enum(USAGE_UNITS),
    startPriceMicros: positiveMicros,
    floorPriceMicros: nonNegativeMicros,
    scaleTokens: z.number().positive().max(1e9),
})
    .refine((curve) => curve.floorPriceMicros <= curve.startPriceMicros, {
    path: ['floorPriceMicros'],
    message: 'the floor price cannot be above the start price',
});
/** Metered tokens in price bands (ascending, the last one open), with an optional minimum per period. */
export const meteredGraduatedSchema = z
    .strictObject({
    kind: z.literal('metered_graduated'),
    key: componentKey,
    label,
    unit: z.enum(USAGE_UNITS),
    bands: z
        .array(z.strictObject({
        /** The band ends at this many tokens of the period; null for the last band. */
        upToTokens: z.number().int().positive().safe().nullable(),
        unitPriceMicros: nonNegativeMicros,
    }))
        .min(1)
        .max(20),
    minimumMicros: nonNegativeMicros.default(0),
})
    .superRefine((graduated, ctx) => {
    graduated.bands.forEach((band, i) => {
        const last = i === graduated.bands.length - 1;
        if (last !== (band.upToTokens === null)) {
            ctx.addIssue({
                code: 'custom',
                path: ['bands', i, 'upToTokens'],
                message: 'only the last band is open (null)',
            });
        }
        const previous = graduated.bands[i - 1]?.upToTokens;
        if (band.upToTokens !== null && previous != null && band.upToTokens <= previous) {
            ctx.addIssue({ code: 'custom', path: ['bands', i, 'upToTokens'], message: 'bands ascend' });
        }
    });
});
/** The condition of a conditional discount. One kind today: every listed module enabled by the organization. */
export const discountConditionSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('modules_enabled'), moduleKeys: z.array(moduleKey).min(1).max(20) }),
]);
/**
 * How a conditional discount counts a period in which its condition held part of the time. The owners have not
 * decided (design section 6, item 3), so it is a field:
 * - whole_period: the whole discount when the condition held at the end of every day of the period, none otherwise;
 * - by_day: the discount times the share of the period's days at whose end the condition held.
 * A day is 24 hours counted from the period's start (its anchor's time of day), the last one cut at the period's end.
 */
export const DISCOUNT_PRORATIONS = ['whole_period', 'by_day'];
/** A percentage off another (metered) component while a condition holds. Only ever lowers the bill. */
export const conditionalDiscountSchema = z.strictObject({
    kind: z.literal('conditional_discount'),
    key: componentKey,
    label,
    /** The key of the metered component it reduces. */
    appliesTo: componentKey,
    /** 1000 basis points = 10 percent. */
    percentBasisPoints: z.number().int().min(1).max(10_000),
    condition: discountConditionSchema,
    proration: z.enum(DISCOUNT_PRORATIONS),
});
export const priceComponentSchema = z.discriminatedUnion('kind', [
    flatRecurringSchema,
    licensedQuantitySchema,
    meteredCurveSchema,
    meteredGraduatedSchema,
    conditionalDiscountSchema,
]);
/* ------------------------------------------------------------------------------------------------ */
/* Token estimates and the billable basis                                                            */
/* ------------------------------------------------------------------------------------------------ */
/**
 * The tokens one action uses at the reference level (Seasoned), and, for actions that have levels, the multiplier
 * of every level in thousandths (Steady 800 = 0.8). The level is a module setting; it changes how many tokens an
 * action uses, never the price of a token. The estimate on screen, the reservation and the clamp read this table.
 */
export const tokenEstimateSchema = z.strictObject({
    action: billableActionSchema,
    /** The module that performs the action in this plan's product (enterprise.quoting). */
    moduleKey,
    referenceMillitokens: z.number().int().positive().safe(),
    levelMultipliersMilli: z
        .record(intelligenceLevelSchema, z.number().int().positive().max(100_000))
        .refine((multipliers) => INTELLIGENCE_LEVELS.every((level) => multipliers[level] !== undefined), {
        message: `a multiplier for every level (${INTELLIGENCE_LEVELS.join(', ')})`,
    })
        .optional(),
    /**
     * Unreleased: units of the action included in every period at no tokens (300 assistant replies a month, design
     * section 6 item 17). The first units of a period, in the order they are admitted or recorded, bill nothing and hold
     * no room under the spend cap; every unit after them bills the estimate as usual. Absent means none included.
     */
    includedPerPeriod: z.number().int().positive().max(1_000_000).optional(),
});
/**
 * What a usage event bills (design section 3, a field because the owners have not decided, section 6 item 2):
 * - estimate: the published estimate of the action at its level;
 * - metered_clamped: the metered tokens clamped between lowerMultipleMilli and upperMultipleMilli thousandths of
 *   the estimate (the spec recommends 500 and 2000). The band always holds the estimate.
 */
export const billableBasisSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('estimate') }),
    z
        .strictObject({
        kind: z.literal('metered_clamped'),
        lowerMultipleMilli: z.number().int().nonnegative().max(1000),
        upperMultipleMilli: z.number().int().min(1000).max(100_000),
    })
        .refine((basis) => basis.lowerMultipleMilli <= basis.upperMultipleMilli, {
        path: ['upperMultipleMilli'],
        message: 'the upper multiple cannot be below the lower one',
    }),
]);
/** A trial: days from the contract start, a card required up front, nothing charged (no free plan). */
export const trialTermsSchema = z.strictObject({
    days: z.number().int().min(1).max(90),
    cardRequired: z.boolean(),
    /**
     * Unreleased (spec decision 15): the trial's free usage is bounded by a token budget (1,500 tokens on the Trace
     * Enterprise plan). Work past it waits (admission trial_budget_used) until the trial ends. Absent: no budget.
     */
    tokenBudget: z.number().int().positive().max(1_000_000_000).optional(),
});
/** Self-serve up to a number of shipments a period; past that an Enterprise prompt, never a block. */
export const selfServeTermsSchema = z.strictObject({
    maxShipmentsPerPeriod: z.number().int().positive().max(1_000_000),
    beyond: z.enum(['enterprise_prompt']),
});
export const BILLING_INTERVALS = ['month'];
/** Everything a plan version prices and allows. Immutable once the version is published. */
export const planVersionContentSchema = z
    .strictObject({
    currency: billingCurrencySchema,
    billingInterval: z.enum(BILLING_INTERVALS),
    components: z.array(priceComponentSchema).min(1).max(20),
    tokenEstimates: z.array(tokenEstimateSchema).max(50),
    billableBasis: billableBasisSchema,
    /** The modules the version allows (the commercial layer of module access). */
    allowedModules: z.array(moduleKey).max(100),
    trial: trialTermsSchema.nullable(),
    selfServe: selfServeTermsSchema.nullable(),
    /** Where the numbers come from (a label shown with every view of the version). */
    source: z.string().min(3).max(300),
})
    .superRefine((content, ctx) => {
    const keys = new Set();
    content.components.forEach((component, i) => {
        if (keys.has(component.key)) {
            ctx.addIssue({
                code: 'custom',
                path: ['components', i, 'key'],
                message: 'component keys are unique',
            });
        }
        keys.add(component.key);
    });
    const metered = content.components.filter((c) => c.kind === 'metered_curve' || c.kind === 'metered_graduated');
    if (metered.length > 1) {
        ctx.addIssue({
            code: 'custom',
            path: ['components'],
            message: 'at most one component prices the tokens',
        });
    }
    content.components.forEach((component, i) => {
        if (component.kind === 'conditional_discount' && !metered.some((m) => m.key === component.appliesTo)) {
            ctx.addIssue({
                code: 'custom',
                path: ['components', i, 'appliesTo'],
                message: 'a discount applies to the metered component of the version',
            });
        }
    });
    const actions = new Set();
    content.tokenEstimates.forEach((estimate, i) => {
        if (actions.has(estimate.action)) {
            ctx.addIssue({
                code: 'custom',
                path: ['tokenEstimates', i, 'action'],
                message: 'one estimate per action',
            });
        }
        actions.add(estimate.action);
    });
});
/* ------------------------------------------------------------------------------------------------ */
/* Plans and plan versions (staff)                                                                   */
/* ------------------------------------------------------------------------------------------------ */
/** A catalog plan is sold to anyone (Trace Enterprise); a custom plan belongs to one organization's deal. */
export const PLAN_KINDS = ['catalog', 'custom'];
/** A draft is editable; a published version never changes again (the database refuses it). */
export const PLAN_VERSION_STATUSES = ['draft', 'published'];
/** Products that are billed (Trace Admin is not a product anyone buys). */
export const billedProductKeySchema = productKeySchema.exclude(['admin']);
const planKey = z
    .string()
    .regex(/^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/, 'lowercase letters, digits and hyphens');
export const planVersionSummarySchema = z.object({
    id: z.uuid(),
    planId: z.uuid(),
    planKey: z.string(),
    planName: z.string(),
    planKind: z.enum(PLAN_KINDS),
    productKey: billedProductKeySchema,
    /** The organization of a custom plan; null for a catalog plan. */
    organizationId: z.uuid().nullable(),
    version: z.number().int().positive(),
    status: z.enum(PLAN_VERSION_STATUSES),
    /** Counts the edits of a draft (optimistic concurrency); frozen at publication. */
    revision: z.number().int().positive(),
    /** sha256 of the content: what publishing confirms. */
    contentDigest: sha256Hex,
    source: z.string(),
    createdBy: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    publishedBy: z.string().nullable(),
    publishedAt: z.iso.datetime().nullable(),
});
export const planVersionSchema = planVersionSummarySchema.extend({ content: planVersionContentSchema });
/** GET plan versions: catalog and custom, newest first, filtered by product, status or organization. */
export const planVersionListQuerySchema = z.object({
    productKey: billedProductKeySchema.optional(),
    status: z.enum(PLAN_VERSION_STATUSES).optional(),
    organizationId: z.uuid().optional(),
});
export const planVersionListSchema = z.object({ items: z.array(planVersionSummarySchema) });
/**
 * POST a draft: the next version of a plan that exists, or version 1 of a new plan (a custom plan names its
 * organization, a catalog plan none), from content or from a copy of another version.
 */
export const planVersionDraftCreateSchema = z
    .strictObject({
    plan: z.union([
        z.strictObject({ planId: z.uuid() }),
        z.strictObject({
            key: planKey,
            name: z.string().min(2).max(120),
            productKey: billedProductKeySchema,
            kind: z.enum(PLAN_KINDS),
            organizationId: z.uuid().optional(),
        }),
    ]),
    fromVersionId: z.uuid().optional(),
    content: planVersionContentSchema.optional(),
    reason,
})
    .superRefine((request, ctx) => {
    if ((request.fromVersionId === undefined) === (request.content === undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['content'],
            message: 'give the content or fromVersionId, one of them',
        });
    }
    if ('kind' in request.plan &&
        (request.plan.kind === 'custom') !== (request.plan.organizationId !== undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['plan', 'organizationId'],
            message: 'a custom plan names its organization, a catalog plan none',
        });
    }
});
/** PATCH a draft: its whole content, the revision read, and a reason. A published version answers 409. */
export const planVersionDraftChangeSchema = z.strictObject({
    content: planVersionContentSchema,
    expectedRevision: z.number().int().positive(),
    reason,
});
/**
 * POST publish: the confirmation is the contentDigest of the draft as it was reviewed. A draft changed since
 * answers 412 and stays a draft, so nobody publishes numbers they did not see.
 */
export const planVersionPublishSchema = z.strictObject({ confirmDigest: sha256Hex, reason });
/* ------------------------------------------------------------------------------------------------ */
/* Contracts                                                                                         */
/* ------------------------------------------------------------------------------------------------ */
/** Computed when read: scheduled (starts later), trialing, active, ended. */
export const CONTRACT_STATUSES = ['scheduled', 'trialing', 'active', 'ended'];
export const BILLING_PROVIDERS = ['fake', 'stripe'];
export const billingProviderNameSchema = z.enum(BILLING_PROVIDERS);
export const spendCapSchema = z.object({ amountMicros: positiveMicros, currency: billingCurrencySchema });
/** A billing period of a contract: the trial, then months from the billing anchor. */
export const billingPeriodSchema = z.object({
    start: z.iso.datetime(),
    end: z.iso.datetime(),
    kind: z.enum(['trial', 'regular']),
});
export const contractSchema = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    productKey: billedProductKeySchema,
    planVersion: planVersionSummarySchema.pick({
        id: true,
        planKey: true,
        planName: true,
        version: true,
        status: true,
    }),
    status: z.enum(CONTRACT_STATUSES),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime().nullable(),
    trialEndsAt: z.iso.datetime().nullable(),
    trialCardRequired: z.boolean(),
    /**
     * Unreleased (additive): staff ended the trial before the plan's trial days ran out (billing then started at
     * trialEndsAt). Defaults to false, so a client of this version still reads an older server.
     */
    trialEndedEarly: z.boolean().default(false),
    /** Where the monthly periods start: the end of the trial, or the start. */
    billingAnchor: z.iso.datetime(),
    carrierConnections: z.number().int().nonnegative(),
    spendCap: spendCapSchema.nullable(),
    approvedBy: z.string().nullable(),
    approvedAt: z.iso.datetime(),
    reason: z.string(),
    /** Optimistic concurrency of changes. */
    version: z.number().int().positive(),
    currentPeriod: billingPeriodSchema.nullable(),
    provider: z
        .object({
        name: billingProviderNameSchema,
        customerRef: z.string().nullable(),
        subscriptionRef: z.string().nullable(),
        status: z.string().nullable(),
    })
        .nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const contractListSchema = z.object({ items: z.array(contractSchema) });
/**
 * POST a contract (staff with staff.billing.manage, who approve it by creating it): a published plan version of
 * the product, the start (and end, at a period boundary), the trial of the plan version when withTrial, the
 * purchased carrier connections and the monthly spend cap in micros of the plan's currency (null: no cap).
 */
export const contractCreateSchema = z.strictObject({
    productKey: billedProductKeySchema,
    planVersionId: z.uuid(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime().nullable().default(null),
    withTrial: z.boolean().default(false),
    carrierConnections: z.number().int().nonnegative().max(100_000).default(0),
    spendCapMicros: positiveMicros.nullable(),
    reason,
});
/**
 * PATCH a contract: the spend cap (a lowered cap blocks new admissions only; work already admitted finishes), the
 * purchased carrier connections, the end (at a period boundary). At least one of them, the version read, a reason.
 */
export const contractChangeSchema = z
    .strictObject({
    expectedVersion: z.number().int().positive(),
    spendCapMicros: positiveMicros.nullable().optional(),
    carrierConnections: z.number().int().nonnegative().max(100_000).optional(),
    endsAt: z.iso.datetime().nullable().optional(),
    reason,
})
    .refine((change) => change.spendCapMicros !== undefined ||
    change.carrierConnections !== undefined ||
    change.endsAt !== undefined, { path: ['spendCapMicros'], message: 'change the spend cap, the carrier connections or the end' });
/**
 * Unreleased (additive): POST end a running trial now, so billing starts now (spec section 2.5). With the version read
 * and a reason. The customer must have a card on file at the billing provider (409 otherwise: they add one through
 * Manage payment first); the provider ends the subscription's trial and invoices the first paid period at once.
 */
export const contractTrialEndSchema = z.strictObject({
    expectedVersion: z.number().int().positive(),
    reason,
});
/* ------------------------------------------------------------------------------------------------ */
/* Usage events, reservations and settlement (acting services, internal API)                         */
/* ------------------------------------------------------------------------------------------------ */
/** The longest a reservation may be held before it expires (and stops counting against the cap). */
export const MAX_RESERVATION_TTL_SECONDS = 3 * 24 * 60 * 60;
export const DEFAULT_RESERVATION_TTL_SECONDS = 24 * 60 * 60;
/** Raw model tokens and provider costs: recorded for margin analysis, never billed. */
export const providerCostSchema = z.strictObject({
    provider: z.string().regex(/^[a-z0-9][a-z0-9_.-]{0,59}$/),
    model: z.string().min(1).max(120),
    inputTokens: z.number().int().nonnegative().safe(),
    outputTokens: z.number().int().nonnegative().safe(),
    costMicros: nonNegativeMicros,
    currency: billingCurrencySchema,
});
/** The work an event or a reservation is about. The product is the module's. */
const workFields = {
    organizationId: z.uuid(),
    moduleKey,
    action: billableActionSchema,
    /** The level the work runs at; the organization's configuration of the module when absent. */
    level: intelligenceLevelSchema.optional(),
    /** Units of the action: messages, call minutes. Once-per-shipment actions are one unit. */
    quantity: z.number().int().min(1).max(10_000).default(1),
    reference: usageReferenceSchema,
};
function oncePerShipment(request, ctx) {
    if (!isOncePerShipment(request.action))
        return;
    if (request.reference.type !== 'shipment') {
        ctx.addIssue({
            code: 'custom',
            path: ['reference', 'type'],
            message: `${request.action} counts once per shipment`,
        });
    }
    if (request.quantity !== 1) {
        ctx.addIssue({
            code: 'custom',
            path: ['quantity'],
            message: `${request.action} counts once per shipment`,
        });
    }
}
/**
 * POST record usage: billable work that needed no reservation (the contract has no spend cap). A contract with a
 * cap refuses it (409): work it bills must be admitted first. Retries with the same eventId are no-ops. An eventId is
 * the producer's, unique within its organization and producer. Late usage of a closed trial is refused (409): work of a
 * trial is never billed.
 */
export const usageRecordRequestSchema = z
    .strictObject({
    eventId: producerKey,
    ...workFields,
    /** The metered tokens, when known (required under the metered_clamped basis). */
    meteredMillitokens: millitokensSchema.optional(),
    occurredAt: z.iso.datetime(),
    providerCosts: z.array(providerCostSchema).max(50).optional(),
})
    .superRefine(oncePerShipment);
/**
 * POST admit a reservation, before work that can create billable tokens: the most the work can bill (the
 * estimate, or the estimate times the upper clamp). admissionKey makes a retry return the same reservation.
 */
export const reservationAdmitRequestSchema = z
    .strictObject({
    admissionKey: producerKey,
    ...workFields,
    ttlSeconds: z.number().int().min(60).max(MAX_RESERVATION_TTL_SECONDS).optional(),
})
    .superRefine(oncePerShipment);
/**
 * POST settle: the work completed; the reservation becomes a usage event that never bills more than it held. Only the
 * service that admitted the work settles or releases it (403 otherwise, audited).
 */
export const reservationSettleRequestSchema = z.strictObject({
    eventId: producerKey,
    meteredMillitokens: millitokensSchema.optional(),
    /** When the work first occurred; the admission time when absent. */
    occurredAt: z.iso.datetime().optional(),
    providerCosts: z.array(providerCostSchema).max(50).optional(),
});
/** POST release: the work ended without billable work. */
export const reservationReleaseRequestSchema = z.strictObject({ reason: reason.optional() });
export const usageEventSchema = z.object({
    id: z.uuid(),
    eventId: z.string(),
    organizationId: z.uuid(),
    /** Null for an unbilled event (billed false): an internal organization's usage names no contract. */
    contractId: z.uuid().nullable(),
    planVersionId: z.uuid().nullable(),
    productKey: billedProductKeySchema,
    moduleKey: z.string(),
    action: billableActionSchema,
    unit: z.enum(USAGE_UNITS),
    level: intelligenceLevelSchema.nullable(),
    quantity: z.number().int().positive(),
    /** Unreleased: the units of quantity the period's allowance covered (they bill nothing); 0 without an allowance. */
    includedQuantity: z.number().int().nonnegative().default(0),
    estimatedMillitokens: millitokensSchema,
    meteredMillitokens: millitokensSchema.nullable(),
    billableMillitokens: millitokensSchema,
    reference: usageReferenceSchema,
    occurredAt: z.iso.datetime(),
    recordedAt: z.iso.datetime(),
    /** The billing period it belongs to. */
    periodStart: z.iso.datetime(),
    producer: z.string(),
    reservationId: z.uuid().nullable(),
    /**
     * Internal organizations (spec 2026-10-18 piece 1): false for usage of a billing exempt organization, recorded and
     * never billed (no contract, zero billable tokens). Optional so a client reading an older platform-api still parses.
     */
    billed: z.boolean().optional(),
});
/** The answer to record usage; duplicate is true when the eventId was recorded before (a retry). */
export const usageRecordResponseSchema = z.object({ usageEvent: usageEventSchema, duplicate: z.boolean() });
export const RESERVATION_STATUSES = ['held', 'settled', 'released', 'expired'];
export const reservationSchema = z.object({
    id: z.uuid(),
    admissionKey: z.string(),
    organizationId: z.uuid(),
    contractId: z.uuid(),
    periodStart: z.iso.datetime(),
    periodEnd: z.iso.datetime(),
    moduleKey: z.string(),
    action: billableActionSchema,
    level: intelligenceLevelSchema.nullable(),
    quantity: z.number().int().positive(),
    /** Unreleased: the units of quantity the period's allowance covers (they reserve and bill nothing). */
    includedQuantity: z.number().int().nonnegative().default(0),
    reference: usageReferenceSchema,
    /** m: the most the work can bill. */
    reservedMillitokens: millitokensSchema,
    /** cost(C + m) - cost(C) at admission: the hold shown to people (the guarantee rests on token counts). */
    hold: z.object({ amountMicros: nonNegativeMicros, currency: billingCurrencySchema }),
    status: z.enum(RESERVATION_STATUSES),
    admittedAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    settledAt: z.iso.datetime().nullable(),
    releasedAt: z.iso.datetime().nullable(),
    expiredAt: z.iso.datetime().nullable(),
    usageEventId: z.uuid().nullable(),
});
/** The spend cap of a period as an admission saw it. */
export const spendCapStatusSchema = z.object({
    capMicros: positiveMicros,
    currency: billingCurrencySchema,
    periodStart: z.iso.datetime(),
    periodEnd: z.iso.datetime(),
    /** C: the period's billed tokens plus the tokens still reserved. */
    committedMillitokens: millitokensSchema,
    committedCostMicros: nonNegativeMicros,
    /** The cap minus cost(C); negative after a lowered cap. */
    remainingMicros: microsSchema,
    /** m, the tokens the refused work asked for (absent in views). */
    requestedMillitokens: millitokensSchema.optional(),
});
/**
 * Why an admitted request reserved nothing: no contract is in force for the module's product, the plan version
 * does not price the action, the action already counted for this shipment, or (internal organizations, spec
 * 2026-10-18 piece 1) the organization is billing exempt: nothing is reserved or capped, and its usage is recorded
 * unbilled (POST usage-events). A client of an older version cannot parse billing_exempt: products vendor this one
 * before an exempt organization uses them.
 */
export const NOT_BILLABLE_REASONS = [
    'no_contract',
    'not_priced',
    'already_counted',
    'billing_exempt',
];
/** Unreleased (spec decision 15): a trial's token budget against what its work used and holds, in millitokens. */
export const trialBudgetStatusSchema = z.object({
    budgetMillitokens: z.number().int().nonnegative(),
    committedMillitokens: z.number().int().nonnegative(),
    requestedMillitokens: z.number().int().nonnegative(),
    trialEndsAt: z.iso.datetime(),
});
/**
 * The answer to an admission. admitted false is spend_capped: cost(C + m) does not fit under the cap, so the work
 * is not admitted and the organization is told (platform.billing.spend_cap_reached).
 */
export const reservationAdmitResponseSchema = z.discriminatedUnion('admitted', [
    z.object({
        admitted: z.literal(true),
        reservation: reservationSchema.nullable(),
        notBillable: z.enum(NOT_BILLABLE_REASONS).nullable(),
        decidedAt: z.iso.datetime(),
    }),
    z.discriminatedUnion('reason', [
        z.object({
            admitted: z.literal(false),
            reason: z.literal('spend_capped'),
            message: z.string(),
            spendCap: spendCapStatusSchema,
            decidedAt: z.iso.datetime(),
        }),
        // 0.9.0, review of milestone 3: the contract's subscription no longer collects (canceled, unpaid or
        // incomplete_expired at the provider), so no new work is admitted until billing is restored.
        z.object({
            admitted: z.literal(false),
            reason: z.literal('collection_stopped'),
            message: z.string(),
            providerStatus: z.string(),
            decidedAt: z.iso.datetime(),
        }),
        // Unreleased (spec decision 15): the trial's token budget is used, so new work waits until the trial ends. A
        // client of an older version cannot parse this answer: products must vendor the version that names it.
        z.object({
            admitted: z.literal(false),
            reason: z.literal('trial_budget_used'),
            message: z.string(),
            trialBudget: trialBudgetStatusSchema,
            decidedAt: z.iso.datetime(),
        }),
    ]),
]);
export const reservationSettleResponseSchema = z.object({
    reservation: reservationSchema,
    usageEvent: usageEventSchema,
});
export const reservationReleaseResponseSchema = z.object({ reservation: reservationSchema });
/**
 * Internal organizations (spec 2026-10-18 piece 1): Trace's own organization, and later demo or partner ones, use
 * every module of their products with no contract and no card; their usage is recorded unbilled and they are never
 * linked to the billing provider. Staff with staff.billing.manage set or clear it with a reason (PUT, audited); 409
 * contract_in_force while a contract is in force or scheduled.
 */
export const billingExemptionChangeSchema = z.strictObject({ exempt: z.boolean(), reason });
export const billingExemptionSchema = z.object({
    organizationId: z.uuid(),
    exempt: z.boolean(),
    /** Why it is not billed; null when it is billed. */
    reason: z.string().nullable(),
});
/* ------------------------------------------------------------------------------------------------ */
/* Usage views, adjustments and rating (staff)                                                       */
/* ------------------------------------------------------------------------------------------------ */
/** GET usage and line items of a period: a contract (the first one in force when absent) and a period start. */
export const billingPeriodQuerySchema = z.object({
    contractId: z.uuid().optional(),
    /** The start of the period; the current period when absent. */
    periodStart: z.iso.datetime().optional(),
    cursor: z.string().max(500).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const periodUsageSchema = z.object({
    contractId: z.uuid(),
    period: billingPeriodSchema,
    status: z.enum(['open', 'closed']),
    billableMillitokens: millitokensSchema,
    heldMillitokens: millitokensSchema,
    committedMillitokens: millitokensSchema,
    spendCap: spendCapStatusSchema.nullable(),
    byAction: z.array(z.object({
        action: billableActionSchema,
        moduleKey: z.string(),
        events: z.number().int().nonnegative(),
        billableMillitokens: millitokensSchema,
        /** Unreleased: units the period's allowance covered, and the allowance (null when the action has none). */
        includedQuantity: z.number().int().nonnegative().default(0),
        includedPerPeriod: z.number().int().positive().nullable().default(null),
    })),
    /** Distinct shipments with usage in the period, against the self-serve terms (an Enterprise prompt past them). */
    shipments: z.number().int().nonnegative(),
    beyondSelfServe: z.boolean(),
    /** Usage events, newest first (keyset pages). */
    events: z.array(usageEventSchema),
    nextCursor: z.string().nullable(),
});
export const ADJUSTMENT_KINDS = ['credit', 'debit'];
export const ADJUSTMENT_STATUSES = ['pending', 'approved', 'rejected'];
/**
 * POST an adjustment: a credit or a debit in micros of the contract's currency on an open period, with a reason.
 * Corrections are adjustments, never edits. Every adjustment waits for the approval of another staff member with
 * staff.billing.manage, so one that would exceed the spend cap never applies unseen.
 */
export const adjustmentCreateSchema = z.strictObject({
    contractId: z.uuid(),
    periodStart: z.iso.datetime(),
    kind: z.enum(ADJUSTMENT_KINDS),
    amountMicros: positiveMicros,
    reason,
});
export const adjustmentDecisionSchema = z.strictObject({ reason });
export const adjustmentSchema = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    contractId: z.uuid(),
    periodStart: z.iso.datetime(),
    kind: z.enum(ADJUSTMENT_KINDS),
    amountMicros: positiveMicros,
    currency: billingCurrencySchema,
    reason: z.string(),
    status: z.enum(ADJUSTMENT_STATUSES),
    requestedBy: z.string().nullable(),
    /**
     * The requester's user id: Trace Admin offers no decision on one's own request by id, never by display name.
     * Defaults to null so a client of 0.11.0 still reads a server from before the field (additive, like includedQuantity).
     */
    requestedById: z.uuid().nullable().default(null),
    requestedAt: z.iso.datetime(),
    decidedBy: z.string().nullable(),
    decidedAt: z.iso.datetime().nullable(),
    decisionReason: z.string().nullable(),
    /** A debit that, with the period's usage, goes past the spend cap: shown to whoever approves it. */
    exceedsSpendCap: z.boolean(),
});
export const adjustmentListSchema = z.object({ items: z.array(adjustmentSchema) });
export const LINE_ITEM_KINDS = [
    'base',
    'licensed_quantity',
    'tokens',
    'discount',
    'adjustment',
    'trial_credit',
];
export const ratedLineItemSchema = z.object({
    position: z.number().int().positive(),
    kind: z.enum(LINE_ITEM_KINDS),
    componentKey: z.string().nullable(),
    description: z.string(),
    quantity: z
        .object({
        value: z.number().int().nonnegative().safe(),
        unit: z.enum(['millitoken', 'carrier_connection', 'period']),
    })
        .nullable(),
    unitPriceMicros: nonNegativeMicros.nullable(),
    /** Signed: discounts, credits and the trial credit are negative. */
    amountMicros: microsSchema,
    detail: z.record(z.string(), z.unknown()),
});
/**
 * A rated period: its line items at close (status closed, stored and never changed), or a preview of an open
 * period (status open, id and ratedAt null, nothing stored).
 */
export const ratedPeriodSchema = z.object({
    id: z.uuid().nullable(),
    contractId: z.uuid(),
    organizationId: z.uuid(),
    periodStart: z.iso.datetime(),
    periodEnd: z.iso.datetime(),
    kind: z.enum(['trial', 'regular']),
    status: z.enum(['open', 'closed']),
    planVersionId: z.uuid(),
    currency: billingCurrencySchema,
    billableMillitokens: millitokensSchema,
    lines: z.array(ratedLineItemSchema),
    totalMicros: microsSchema,
    ratedAt: z.iso.datetime().nullable(),
});
/* ------------------------------------------------------------------------------------------------ */
/* Billing documents and the billing provider                                                        */
/* ------------------------------------------------------------------------------------------------ */
export const BILLING_DOCUMENT_STATUSES = ['draft', 'open', 'paid', 'void', 'uncollectible'];
const providerInvoiceFields = {
    providerDocumentId: z.string().min(1).max(255),
    status: z.enum(BILLING_DOCUMENT_STATUSES),
    currency: billingCurrencySchema,
    totalMicros: microsSchema,
    amountDueMicros: microsSchema,
    amountPaidMicros: microsSchema,
    periodStart: z.iso.datetime().nullable(),
    periodEnd: z.iso.datetime().nullable(),
    issuedAt: z.iso.datetime().nullable(),
    hostedUrl: z.url().nullable(),
};
/** An invoice of the provider, reconciled back (webhooks and the periodic reconciliation). */
export const billingDocumentSchema = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    contractId: z.uuid().nullable(),
    ratedPeriodId: z.uuid().nullable(),
    provider: billingProviderNameSchema,
    kind: z.literal('invoice'),
    ...providerInvoiceFields,
    updatedAt: z.iso.datetime(),
});
export const billingDocumentListSchema = z.object({ items: z.array(billingDocumentSchema) });
/**
 * A provider event as the platform processes it, normalized from the provider's own shape (a Stripe webhook).
 * livemode is true only in production (live billing); every other environment accepts test mode events only.
 */
const providerEventBase = {
    provider: billingProviderNameSchema,
    providerEventId: z.string().min(1).max(255),
    occurredAt: z.iso.datetime(),
    livemode: z.boolean(),
};
export const billingProviderEventSchema = z.discriminatedUnion('type', [
    z.object({
        ...providerEventBase,
        type: z.literal('invoice.updated'),
        invoice: z.object({
            ...providerInvoiceFields,
            customerRef: z.string().nullable(),
            subscriptionRef: z.string().nullable(),
            /** The contract and rated period an invoice of a closed period names in its metadata (null otherwise). */
            contractId: z.uuid().nullable().default(null),
            ratedPeriodId: z.uuid().nullable().default(null),
        }),
    }),
    z.object({
        ...providerEventBase,
        type: z.literal('subscription.updated'),
        subscription: z.object({
            subscriptionRef: z.string().min(1),
            customerRef: z.string().min(1),
            status: z.string().min(1).max(60),
            trialEndsAt: z.iso.datetime().nullable(),
            /** Unreleased: when the subscription ended (canceled or expired), null while it runs. */
            endedAt: z.iso.datetime().nullable().default(null),
            contractId: z.uuid().nullable(),
        }),
    }),
    z.object({
        ...providerEventBase,
        /**
         * Unreleased (self-serve sign-up, spec 2026-10-18 piece 5): a Checkout session in setup mode completed, so a card was
         * saved for the sign-up its metadata names (null when it names none). A session of any other mode is ignored.
         */
        type: z.literal('checkout.completed'),
        checkout: z.object({
            sessionRef: z.string().min(1),
            customerRef: z.string().nullable(),
            setupIntentRef: z.string().nullable(),
            signupId: z.uuid().nullable(),
        }),
    }),
    z.object({ ...providerEventBase, type: z.literal('ignored'), providerType: z.string().max(120) }),
]);
/* ------------------------------------------------------------------------------------------------ */
/* Routes and events                                                                                 */
/* ------------------------------------------------------------------------------------------------ */
const segment = encodeURIComponent;
/**
 * Unreleased (additive): GET an organization's billing standing for one product (acting services only), read from the
 * same records admission and the module states decide from, at this moment. spendCapped: a module of the product is
 * in the spend_capped state (new work would be refused for the cap). collectionStopped: the product's contract in force
 * no longer collects at the billing provider (new work would be refused with collection_stopped). A product shows
 * notices about such refusals to the organization's administrators and clears them once both are false. Never
 * cached; read-only (no audit, no event).
 */
export const billingStandingQuerySchema = z.strictObject({ productKey: billedProductKeySchema });
export const billingStandingSchema = z.object({
    organizationId: z.uuid(),
    productKey: billedProductKeySchema,
    spendCapped: z.boolean(),
    collectionStopped: z.boolean(),
    checkedAt: z.iso.datetime(),
});
/**
 * Unreleased (additive, self-serve piece 6): GET what an acting service shows an organization's people about the
 * contract they are on, read at this moment. billingExempt: the organization is internal (it never has a contract).
 * contract: the product's contract in force (null with none): its version (a change sends it back), status, start,
 * the running trial (its end, whole days left rounded up, the plan's trial token budget and what the trial period
 * billed or still holds, in millitokens; null outside a trial), the carrier connections bought, the plan's maximum
 * and unit price for them (null when the plan sells none), the spend cap and the currency. Read-only, never cached.
 */
export const contractSummaryQuerySchema = z.strictObject({ productKey: billedProductKeySchema });
export const internalContractSummarySchema = z.object({
    organizationId: z.uuid(),
    productKey: billedProductKeySchema,
    billingExempt: z.boolean(),
    contract: z
        .object({
        id: z.uuid(),
        version: z.number().int().positive(),
        status: z.enum(CONTRACT_STATUSES),
        startsAt: z.iso.datetime(),
        trial: z
            .object({
            endsAt: z.iso.datetime(),
            daysLeft: z.number().int().nonnegative(),
            tokenBudgetMillitokens: z.number().int().nullable(),
            tokensUsedMillitokens: z.number().int().nonnegative(),
        })
            .nullable(),
        carrierConnections: z.number().int().nonnegative(),
        maxCarrierConnections: z.number().int().nullable(),
        carrierConnectionPriceMicros: z.number().int().nullable(),
        spendCapMicros: z.number().int().nullable(),
        currency: billingCurrencySchema,
    })
        .nullable(),
    checkedAt: z.iso.datetime(),
});
/**
 * Unreleased (additive, self-serve piece 6): POST, an acting service changes the carrier connections an organization
 * buys under its contract in force, on behalf of one of its people (actingUserId, a platform user id). Refused 403
 * billing_manage_required unless that person's active membership grants billing.manage, 409 billing_exempt for an
 * internal organization, 404 with no contract in force, 409 on a stale expectedVersion; otherwise exactly the org-admin
 * change (a raise counts in the current period, a lowered number bills from the next). Answers the new summary.
 */
export const internalCarrierConnectionsChangeSchema = z.strictObject({
    productKey: billedProductKeySchema,
    actingUserId: z.uuid(),
    expectedVersion: z.number().int().positive(),
    carrierConnections: z.number().int().nonnegative().max(100_000),
    reason: z.string().min(3).max(500),
});
/**
 * Milestone 3 route table: staff routes under /v1/admin/billing/ (admin audience, staff with MFA, reads with
 * staff.billing.read, changes with staff.billing.manage), routes for acting services under /v1/internal/billing/
 * (service tokens of allowlisted principals), and the provider's webhook (public, verified by its signature).
 */
export const PLATFORM_BILLING_API = {
    planVersions: '/v1/admin/billing/plan-versions',
    planVersion: (versionId) => `/v1/admin/billing/plan-versions/${segment(versionId)}`,
    publishPlanVersion: (versionId) => `/v1/admin/billing/plan-versions/${segment(versionId)}/publish`,
    contracts: (orgId) => `/v1/admin/billing/organizations/${segment(orgId)}/contracts`,
    contract: (orgId, contractId) => `/v1/admin/billing/organizations/${segment(orgId)}/contracts/${segment(contractId)}`,
    /** Unreleased (additive): POST, staff end a contract's running trial now. */
    endContractTrial: (orgId, contractId) => `/v1/admin/billing/organizations/${segment(orgId)}/contracts/${segment(contractId)}/end-trial`,
    usage: (orgId) => `/v1/admin/billing/organizations/${segment(orgId)}/usage`,
    lineItems: (orgId) => `/v1/admin/billing/organizations/${segment(orgId)}/line-items`,
    adjustments: (orgId) => `/v1/admin/billing/organizations/${segment(orgId)}/adjustments`,
    approveAdjustment: (orgId, adjustmentId) => `/v1/admin/billing/organizations/${segment(orgId)}/adjustments/${segment(adjustmentId)}/approve`,
    rejectAdjustment: (orgId, adjustmentId) => `/v1/admin/billing/organizations/${segment(orgId)}/adjustments/${segment(adjustmentId)}/reject`,
    documents: (orgId) => `/v1/admin/billing/organizations/${segment(orgId)}/documents`,
    /** Unreleased (additive): PUT, staff set or clear an organization's billing exemption (internal organizations). */
    billingExemption: (orgId) => `/v1/admin/billing/organizations/${segment(orgId)}/exemption`,
    recordUsage: '/v1/internal/billing/usage-events',
    admitReservation: '/v1/internal/billing/reservations',
    settleReservation: (reservationId) => `/v1/internal/billing/reservations/${segment(reservationId)}/settle`,
    releaseReservation: (reservationId) => `/v1/internal/billing/reservations/${segment(reservationId)}/release`,
    /** Unreleased (additive): GET, an organization's billing standing for a product (acting services). */
    billingStanding: (orgId) => `/v1/internal/billing/organizations/${segment(orgId)}/standing`,
    /** Unreleased (additive, self-serve piece 6): GET, an organization's contract summary for a product (acting services). */
    contractSummary: (orgId) => `/v1/internal/billing/organizations/${segment(orgId)}/contract-summary`,
    /** Unreleased (additive, self-serve piece 6): POST, change the carrier connections bought, for a billing manager. */
    internalCarrierConnections: (orgId) => `/v1/internal/billing/organizations/${segment(orgId)}/carrier-connections`,
    stripeWebhook: '/v1/billing/webhooks/stripe',
};
/** The billing events platform-api publishes through its outbox (also in PLATFORM_EVENT_TYPES). */
export const BILLING_EVENT_TYPES = [
    'platform.billing.plan_version_drafted',
    'platform.billing.plan_version_published',
    'platform.billing.contract_created',
    'platform.billing.contract_changed',
    'platform.billing.adjustment_requested',
    'platform.billing.adjustment_approved',
    'platform.billing.adjustment_rejected',
    'platform.billing.spend_cap_reached',
    'platform.billing.period_rated',
    'platform.billing.document_updated',
    // Review of milestone 3: a contract in force whose subscription stopped collecting (staff are alerted).
    'platform.billing.collection_stopped',
    // Unreleased: a card-required trial lapsed without a card and its contract ended at the trial's end (design section
    // 6 item 14), or could not end because a later period was rated already (staff are alerted either way).
    'platform.billing.trial_lapsed',
    // Unreleased: a trial used its token budget (spec decision 15), once per trial; the organization is told.
    'platform.billing.trial_budget_used',
    // Unreleased: a period passed the plan's self-serve shipments (150 on Trace Enterprise), once per period; billing
    // continues on the curve and sales is told (design section 6 item 6).
    'platform.billing.self_serve_exceeded',
    // Unreleased: staff ended a contract's trial early, so billing started then (spec section 2.5).
    'platform.billing.trial_ended',
    'platform.billing.exemption_changed',
];
//# sourceMappingURL=billing.js.map